import React, { createContext, useContext, useMemo } from 'react'

import { useToast } from '@pnld/components-web'

import { useQueryGetMyAccount } from '@/api/users/account/queries'
import { TGetMyAccountResponse } from '@/api/users/account/types'
import { ROUTES } from '@/config/routes'
import { getErrorDetails } from '@/utils/error'

import { useAuth } from './AuthContext'

interface IUserContextData {
  userProfile: string | undefined
  dsCpf: string | undefined
  userName: string | undefined
  userEmail: string | undefined

  isLoading: boolean
  hasAccess: boolean

  userData: TGetMyAccountResponse | undefined

  refetch: () => void
}

// atualizando para rebuildar
const UserContext = createContext<IUserContextData>({
  userProfile: undefined,
  dsCpf: undefined,
  userName: undefined,
  userEmail: undefined,
  isLoading: true,
  hasAccess: false,
  userData: undefined,
  refetch: () => {},
})

export const UserContextProvider = ({ children }: React.PropsWithChildren) => {
  const toast = useToast()
  const { logout } = useAuth()

  const {
    data: myAccount,
    isLoading,
    error,
    refetch,
  } = useQueryGetMyAccount({
    enabled: true,

    refetchOnMount: 'always',
    staleTime: 0,
    cacheTime: 0,
    keepPreviousData: false,
    notifyOnChangeProps: ['data', 'error', 'isLoading'],

    retry: (failureCount, error) => {
      const status = error.response?.status

      // 429: repetir só gastaria mais do limite de requisições.
      if (status === 401 || status === 403 || status === 429) {
        return false
      }
      return failureCount < 2
    },

    onError: error => {
      const status = error.response?.status

      if (status === 401) {
        logout()
        return
      }

      if (status === 403) {
        window.location.href = ROUTES.ACCESS_DENIED
        return
      }

      toast({
        title: 'Erro ao carregar dados do usuário',
        description: getErrorDetails(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    },
  })

  const contextValue = useMemo(
    () => ({
      userProfile: myAccount?.profile.noProfileName,
      dsCpf: myAccount?.dsCpf,
      userName: myAccount?.noUserName,
      userEmail: myAccount?.dsEmail,

      isLoading,
      hasAccess: !!myAccount && !error,

      userData: myAccount,

      refetch,
    }),
    [myAccount, isLoading, error, refetch]
  )

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  )
}

export const useUser = () => {
  const context = useContext(UserContext)

  if (!context) {
    throw new Error('Hook usado fora do contexto de UserContext')
  }

  return context
}
